/* ============================================================
   MD · Bundle BYO controller (Horizon theme, look & feel Paws Mix and Match)
   - Catalogo + tier preview + sidebar with progress bar
   - Image swap when changing color/size selector
   - Opens Horizon native <cart-drawer-component> on add
   ============================================================ */

(function () {
  'use strict';

  const formatMoneyCLP = (cents) => {
    // Shopify Ajax API returns prices in cents (×100) for CLP. Divide by 100 for display.
    const amount = Math.round((Number(cents) || 0) / 100);
    return '$' + amount.toLocaleString('es-CL').replace(/,/g, '.');
  };

  const variantFeaturedImageUrl = (variant) => {
    if (!variant) return null;
    if (variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
    if (variant.image && variant.image.src) return variant.image.src;
    if (typeof variant.featured_image === 'string') return variant.featured_image;
    return null;
  };

  const ensureProtocol = (url) => {
    if (!url) return url;
    if (url.startsWith('//')) return window.location.protocol + url;
    return url;
  };

  async function refreshAndOpenHorizonCart(addData) {
    // Dispatch event(s) the Horizon cart components listen to
    try {
      const eventNames = ['cart:add', 'cart:added', 'cart-add', 'cartadd'];
      eventNames.forEach((name) => {
        try {
          document.dispatchEvent(new CustomEvent(name, {
            detail: { data: addData, source: 'bundle-byo' },
            bubbles: true,
          }));
        } catch (e) { /* ignore */ }
      });
    } catch (e) { console.warn('[md-byo] dispatch event failed:', e); }

    // Find <cart-drawer-component> Horizon native and call open()
    try {
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }
    } catch (e) { console.warn('[md-byo] drawer.open failed:', e); }

    // Legacy fallback (Dawn or others)
    try {
      const legacyDrawer = document.querySelector('cart-drawer, #CartDrawer');
      if (legacyDrawer) {
        if (typeof legacyDrawer.open === 'function') { legacyDrawer.open(); return true; }
        if (typeof legacyDrawer.showModal === 'function') { legacyDrawer.showModal(); return true; }
        legacyDrawer.setAttribute('open', '');
        return true;
      }
    } catch (e) { /* ignore */ }

    return false;
  }

  document.querySelectorAll('[data-byo-bundle]').forEach((root) => {
    const config = JSON.parse(root.querySelector('[data-byo-config]')?.textContent || '{}');
    const tiers = (config.tiers || []).sort((a, b) => a.min_items - b.min_items);
    const minItems = config.min_items || 2;
    const maxItems = config.max_items || 99;

    const cartList = root.querySelector('[data-byo-cart-list]');
    const cartEmpty = root.querySelector('[data-byo-cart-empty]');
    const countEl = root.querySelector('[data-byo-count]');
    const countPluralEl = root.querySelector('[data-byo-count-plural]');
    const subtotalEl = root.querySelector('[data-byo-subtotal]');
    const discountRow = root.querySelector('[data-byo-discount-row]');
    const discountLabelEl = root.querySelector('[data-byo-discount-label]');
    const discountAmountEl = root.querySelector('[data-byo-discount-amount]');
    const totalEl = root.querySelector('[data-byo-total]');
    const ctaBtn = root.querySelector('[data-byo-checkout]');
    const progressFill = root.querySelector('[data-byo-progress-fill]');
    const progressMsg = root.querySelector('[data-byo-progress-msg]');
    const tierChips = root.querySelectorAll('.byo__tier-chip');
    const progressTiers = root.querySelectorAll('[data-progress-tier-min]');

    const state = { items: [] };

    const parseJSONAttr = (str, fallback) => {
      if (!str) return fallback;
      try { return JSON.parse(str); } catch { return fallback; }
    };

    const resolveVariantForCard = (card) => {
      const variants = parseJSONAttr(card.dataset.productVariants, null);
      if (!variants) return null;
      const optionSelects = card.querySelectorAll('[data-byo-option-name]');
      if (optionSelects.length === 0) return variants[0];
      const selectedValues = {};
      optionSelects.forEach((sel) => { selectedValues[sel.dataset.byoOptionName] = sel.value; });
      const options = parseJSONAttr(card.dataset.productOptions, []);
      const matched = variants.find((v) =>
        v.options.every((optVal, idx) => {
          const optName = options[idx] && options[idx].name;
          return selectedValues[optName] === optVal;
        })
      );
      return matched || variants[0];
    };

    // Disable <option> values que llevan a variants no disponibles
    const refreshUnavailableOptions = (card) => {
      const variants = parseJSONAttr(card.dataset.productVariants, null);
      const options = parseJSONAttr(card.dataset.productOptions, []);
      if (!variants || !options.length) return;

      const currentByName = {};
      card.querySelectorAll('[data-byo-option-name]').forEach((sel) => {
        currentByName[sel.dataset.byoOptionName] = sel.value;
      });

      card.querySelectorAll('[data-byo-option-name]').forEach((sel) => {
        const optName = sel.dataset.byoOptionName;
        Array.from(sel.options).forEach((opt) => {
          const candidate = Object.assign({}, currentByName);
          candidate[optName] = opt.value;
          const match = variants.find((v) =>
            v.options.every((optVal, idx) => {
              const name = options[idx] && options[idx].name;
              return candidate[name] === optVal;
            })
          );
          opt.disabled = !(match && match.available);
        });
      });
    };

    const updateCardImage = (card, variant) => {
      const imgEl = card.querySelector('.byo__product-image img');
      if (!imgEl || !variant) return;
      // Try multiple sources: variant.featured_image, variant.image, variant.featured_media.preview_image
      let newUrl = variantFeaturedImageUrl(variant);
      if (!newUrl && variant.featured_media) {
        const fm = variant.featured_media;
        if (fm.preview_image && fm.preview_image.src) newUrl = fm.preview_image.src;
        else if (fm.src) newUrl = fm.src;
      }
      if (!newUrl) return;
      newUrl = ensureProtocol(newUrl);
      const currentSrc = imgEl.currentSrc || imgEl.src || '';
      if (currentSrc.split('?')[0] === newUrl.split('?')[0]) return;
      imgEl.removeAttribute('srcset');
      imgEl.src = newUrl;
    };

    const collectSelectedOptions = (card) => {
      const result = {};
      card.querySelectorAll('[data-byo-option-name]').forEach((sel) => {
        result[sel.dataset.byoOptionName] = sel.value;
      });
      return result;
    };

    const addItem = (card) => {
      const variant = resolveVariantForCard(card);
      if (!variant || !variant.available) {
        alert('Este producto/variante no está disponible.');
        return;
      }
      if (state.items.length >= maxItems) {
        alert('Ya alcanzaste el máximo de ' + maxItems + ' artículos.');
        return;
      }
      const lineId = 'line_' + Math.random().toString(36).slice(2, 9);
      state.items.push({
        lineId, cardEl: card,
        variantId: variant.id,
        productId: card.dataset.productId,
        productHandle: card.dataset.productHandle,
        title: card.dataset.productTitle,
        variantTitle: variant.title === 'Default Title' ? '' : variant.title,
        price: Number(variant.price) || Number(card.dataset.productDefaultPrice) || 0,
        image: variantFeaturedImageUrl(variant) || card.dataset.productImage,
        optionSelections: collectSelectedOptions(card),
      });
      card.dataset.byoAdded = 'true';
      render();
    };

    const removeItem = (lineId) => {
      const item = state.items.find((i) => i.lineId === lineId);
      if (item && item.cardEl) {
        const stillUsed = state.items.some((i) => i.lineId !== lineId && i.variantId === item.variantId);
        if (!stillUsed) item.cardEl.dataset.byoAdded = 'false';
      }
      state.items = state.items.filter((i) => i.lineId !== lineId);
      render();
    };

    const currentTier = () => {
      const count = state.items.length;
      let active = null;
      tiers.forEach((t) => { if (count >= t.min_items) active = t; });
      return active;
    };

    const nextTier = () => {
      const count = state.items.length;
      return tiers.find((t) => count < t.min_items) || null;
    };

    const render = () => {
      const count = state.items.length;
      const subtotal = state.items.reduce((s, i) => s + i.price, 0);
      const tier = currentTier();
      const discountAmount = tier ? Math.round(subtotal * (tier.discount_pct / 100)) : 0;
      const total = subtotal - discountAmount;

      if (countEl) countEl.textContent = count;
      if (countPluralEl) countPluralEl.hidden = count === 1;

      if (cartList) {
        cartList.innerHTML = state.items.map((i) => `
          <li class="byo__cart-item" data-line-id="${i.lineId}">
            <span class="byo__cart-item-image">${i.image ? `<img src="${i.image}" alt="">` : ''}</span>
            <span class="byo__cart-item-info">
              <span class="byo__cart-item-name">${i.title}</span>
              ${i.variantTitle ? `<span class="byo__cart-item-variant">${i.variantTitle}</span>` : ''}
            </span>
            <button type="button" class="byo__cart-item-remove" aria-label="Quitar" data-byo-remove="${i.lineId}">×</button>
          </li>
        `).join('');
        cartList.querySelectorAll('[data-byo-remove]').forEach((btn) => {
          btn.addEventListener('click', () => removeItem(btn.dataset.byoRemove));
        });
      }
      if (cartEmpty) cartEmpty.hidden = count > 0;

      if (subtotalEl) subtotalEl.textContent = formatMoneyCLP(subtotal);
      if (totalEl) totalEl.textContent = formatMoneyCLP(total);
      if (discountRow) discountRow.hidden = !tier;
      if (tier) {
        if (discountLabelEl) discountLabelEl.textContent = `Ahorras ${tier.discount_pct}%`;
        if (discountAmountEl) discountAmountEl.textContent = '-' + formatMoneyCLP(discountAmount);
      }

      tierChips.forEach((chip) => {
        const min = Number(chip.dataset.tierMin);
        chip.dataset.tierActive = (tier && tier.min_items === min) ? 'true' : 'false';
      });

      const maxTierItems = tiers.length > 0 ? tiers[tiers.length - 1].min_items : minItems;
      const pct = Math.min(100, (count / maxTierItems) * 100);
      if (progressFill) progressFill.style.width = pct + '%';

      progressTiers.forEach((pt) => {
        const minVal = Number(pt.dataset.progressTierMin);
        pt.dataset.progressTierReached = (count >= minVal) ? 'true' : 'false';
      });

      if (progressMsg) {
        if (count === 0) {
          progressMsg.textContent = `Añade al menos ${minItems} artículos para activar el descuento`;
        } else if (!tier) {
          const next = nextTier();
          const missing = next ? next.min_items - count : 0;
          progressMsg.textContent = `Agrega ${missing} más para llegar a ${next ? next.discount_pct : ''}% off`;
        } else {
          const next = nextTier();
          if (next) {
            const missing = next.min_items - count;
            progressMsg.textContent = `¡${tier.discount_pct}% off activo! Agrega ${missing} más para ${next.discount_pct}% off`;
          } else {
            progressMsg.textContent = `¡Máximo descuento activo: ${tier.discount_pct}% off!`;
          }
        }
      }

      if (ctaBtn) ctaBtn.disabled = count < minItems;
    };

    const addBundleToCart = async () => {
      if (state.items.length < minItems) return;
      ctaBtn.disabled = true;
      const origText = ctaBtn.textContent;
      ctaBtn.textContent = 'Agregando...';
      try {
        const grouped = {};
        state.items.forEach((i) => {
          if (!grouped[i.variantId]) {
            grouped[i.variantId] = { id: i.variantId, quantity: 0, properties: { '_bundle': config.bundle_handle } };
          }
          grouped[i.variantId].quantity += 1;
        });
        const items = Object.values(grouped);

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          }),
        });
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);
        const addData = await res.json();

        const opened = await refreshAndOpenHorizonCart(addData);
        ctaBtn.disabled = false;
        ctaBtn.textContent = origText;
        if (!opened) {
          console.warn('[md-byo] cart drawer no se pudo abrir; items agregados pero sin drawer visible');
        }
      } catch (err) {
        console.error(err);
        alert('Hubo un problema agregando el bundle. Intenta de nuevo.');
        ctaBtn.disabled = false;
        ctaBtn.textContent = origText;
      }
    };

    // Wire up product cards
    root.querySelectorAll('[data-byo-product]').forEach((card) => {
      // On selector change → update image + refresh availability + update price
      card.querySelectorAll('[data-byo-option-name]').forEach((sel) => {
        sel.addEventListener('change', () => {
          refreshUnavailableOptions(card);
          const v = resolveVariantForCard(card);
          if (v) {
            updateCardImage(card, v);
            // Update displayed price
            const priceEl = card.querySelector('.byo__product-price');
            if (priceEl && v.price != null) priceEl.textContent = formatMoneyCLP(v.price);
            // Update add button availability
            const addBtn = card.querySelector('[data-byo-add]');
            if (addBtn) addBtn.disabled = !v.available;
          }
        });
      });

      // Initial: mark disabled options + initial price/availability
      refreshUnavailableOptions(card);
      // Check if ALL variants are out of stock — disable Añadir permanently
      const allVariants = parseJSONAttr(card.dataset.productVariants, []);
      const anyAvailable = allVariants.length === 0 || allVariants.some(v => v.available);
      const initialV = resolveVariantForCard(card);
      const addBtn = card.querySelector('[data-byo-add]');
      if (addBtn) {
        if (!anyAvailable) {
          addBtn.disabled = true;
          addBtn.setAttribute('data-all-oos', 'true');
          card.setAttribute('data-byo-all-oos', 'true');
        } else if (initialV) {
          addBtn.disabled = !initialV.available;
        }
        addBtn.addEventListener('click', () => addItem(card));
      }
    });

    if (ctaBtn) ctaBtn.addEventListener('click', addBundleToCart);
    render();
  });
})();
